// TEMPORARY (temp/linked-customer): plain functions behind the linked customer screens (no React, easy to test).

export const POLL_FAST_MS = 2000;
export const POLL_SLOW_MS = 5000;
export const HINT_AFTER_MS = 20000;
export const GIVE_UP_AFTER_MS = 120000;

// States that need no more polling: the next step is a person's.
export const isSettled = (state) => ["linked", "failed", "confirm_required", "not_linked"].includes(state);

// What the polling modal should do next. `elapsedMs` = time since polling started.
export const pollPlan = (state, elapsedMs) => {
    if (isSettled(state)) return { keepPolling: false, delayMs: 0, hint: "" };
    if (elapsedMs >= GIVE_UP_AFTER_MS) return { keepPolling: false, delayMs: 0, hint: "Still working in the background. Check the customer list in a moment." };
    if (elapsedMs >= HINT_AFTER_MS) return { keepPolling: true, delayMs: POLL_SLOW_MS, hint: "This is taking longer than usual. You can close this and check the customer list." };
    return { keepPolling: true, delayMs: POLL_FAST_MS, hint: "" };
};

const LABELS = {
    linked: "Linked",
    pending: "Linking...",
    confirm_required: "Needs confirmation",
    failed: "Link failed",
    not_linked: "Not linked"
};
export const stateLabel = (state) => LABELS[state] || "Unknown";

export const stateTone = (state) => ({ linked: "success", pending: "info", confirm_required: "warning", failed: "danger" }[state] || "secondary");

// Short text for the person, never raw codes.
export const stateMessage = (info) => {
    if (!info) return "";
    switch (info.state) {
        case "linked": return info.mode === "existing"
            ? "Linked to an existing Booking Form customer." : "Linked. The customer now exists in the Booking Form.";
        case "pending": return "Creating the customer in the Booking Form...";
        case "confirm_required": return "The Booking Form already has a customer with this phone number. Check it is the same person, then confirm.";
        case "failed": return info.lastError?.message ? `Linking failed: ${info.lastError.message}` : "Linking failed.";
        default: return "This customer is not linked to the Booking Form.";
    }
};

// Retry and Resync are buttons for admins only; this says when each makes sense.
export const canRetry = (info) => info?.state === "failed";
export const canResync = (info) => info?.state === "linked";
export const canVerifyPhones = (info) => info?.state === "linked";

export const syncNote = (info) => {
    if (!info || info.state !== "linked") return "";
    if (info.lastSyncError?.message) return `Last update to the Booking Form failed: ${info.lastSyncError.message}`;
    return info.lastSyncedAt ? "Details are kept up to date in the Booking Form." : "First update to the Booking Form is in progress.";
};

// What the Booking Form did not take on the last update, in plain words (ids/phones are never shown).
const ISSUE_TEXT = {
    "kyc:aadhaar": "Aadhaar was not added: that Aadhaar already belongs to another Booking Form customer.",
    "kyc:dl": "Driving licence was not added: that licence already belongs to another Booking Form customer.",
    "kyc:passport": "Passport was not added: that passport already belongs to another Booking Form customer.",
    email: "Email was not added: it is already used by another Booking Form customer."
};
const SKIP_TEXT = {
    firstName: "first name", lastName: "last name", photo: "photo", email: "email", dob: "date of birth",
    "kyc:aadhaar": "Aadhaar", "kyc:dl": "driving licence", "kyc:passport": "passport"
};
export const syncIssues = (info) => {
    const r = info && info.state === "linked" ? info.syncResult : null;
    if (!r) return [];
    const out = (r.conflicts || []).map((c) => ISSUE_TEXT[c] || "One detail was not added because it clashes with another Booking Form customer.");
    const kept = (r.skipped || []).map((k) => SKIP_TEXT[k]).filter(Boolean);
    if (kept.length) out.push(`Kept the existing Booking Form value for: ${kept.join(", ")}.`);
    return out;
};

// Numbers 2 and 3: Indian 10 digit numbers only, priority 2 or 3.
export const cleanDigits = (value, max) => String(value || "").replace(/\D/g, "").slice(0, max);
export const validateSlot = ({ phone, priority, type }) => {
    if (!/^\d{10}$/.test(String(phone || ""))) return "Enter a 10 digit mobile number";
    if (![2, 3].includes(Number(priority))) return "Choose number 2 or number 3";
    if (!["normal", "whatsapp"].includes(type)) return "Choose how to send the OTP";
    return "";
};
export const slotPayload = ({ phone, priority, type }) => ({ phone: { phone, code: "+91", priority: Number(priority), type } });

// Which of slots 2 and 3 are already verified (from GET /phones data).
export const verifiedSlots = (phones) => {
    const done = { 2: false, 3: false };
    for (const p of phones || []) if (p && p.verified && (p.priority === 2 || p.priority === 3)) done[p.priority] = true;
    return done;
};

// The list chip: one /status answer -> label.
export const chipFor = (entry) => (entry ? { text: stateLabel(entry.state), tone: stateTone(entry.state) } : null);
